import CheckInLogExport from '../CheckInLogExport';
import AdminDateRange from '../AdminDateRange/AdminDateRange';
import AdminPagination from '../AdminPagination';
import AdminState from '../AdminState';
import { ArrowRightIcon, RepeatIcon, ChevronDownIcon, ChevronUpIcon } from '../AdminIcon';

// Components/Tabs/LabSwipeLogTab.jsx

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { AdminToolbar } from "../AdminLayout/AdminLayout";
import Backend from "../../../utils/api.js";
import { Box, Text, VStack, HStack, Badge, IconButton, Collapse, Tag, TagLabel } from '@chakra-ui/react';

import "./LabSwipeLogTab.css";

/**
 * LabSwipeLogTab Component
 * 
 * Displays the swipe in/out log for a specific lab, showing user info
 * and machines used during each session.
 */
function LabSwipeLogTab({ user, labId, pushTab, revert }) {
  const [swipeLog, setSwipeLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState({ preset: 'all', start: null, end: null });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const latestRequest = useRef(0);

  useEffect(() => {
    loadSwipeLog();
    return () => { latestRequest.current += 1; };
  }, [labId, page, dateRange]);

  const loadSwipeLog = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);

    try {
      const response = await Backend.adminGetLabSwipeLog(
        user.UID,
        labId,
        dateRange.start,
        dateRange.end,
        page,
        50
      );

      if (requestId !== latestRequest.current) return;
      if (response.status === 200) {
        setHasLoaded(true);
        setSwipeLog(response.data || []);
        setTotalPages(Math.ceil((response.total || 0) / (response.per_page || 50)) || 1);
        setTotal(response.total || 0);
      } else if (response.status === 403) {
        setError("You do not have permission to view the check in log");
      } else if (response.status === 404) {
        setError("Space not found");
      } else {
        setError("Failed to load check in log");
      }
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error("Error loading check in log:", err);
      setError("Failed to connect to the server");
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  const applyDateRange = range => {
    setPage(1);
    setExpandedRows(new Set());
    setDateRange(range);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (swipeIn, swipeOut) => {
    if (!swipeIn || !swipeOut) return "Active";
    const start = new Date(swipeIn);
    const end = new Date(swipeOut);
    const minutes = Math.round((end - start) / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Slider
      name="Check in log"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`LabSwipeLogTab${loading && hasLoaded ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        <AdminToolbar title={`${labId} - Check in log`} count={<Badge colorScheme="blue">{total} record{total !== 1 ? 's' : ''}</Badge>}>
          <CheckInLogExport spaceId={labId} dateRange={dateRange} isDisabled={loading || Boolean(error)} />
          <IconButton icon={<RepeatIcon />} onClick={() => loadSwipeLog()} aria-label="Refresh check in log" isLoading={loading} variant="outline" />
        </AdminToolbar>

        <Box className="filters-section" mb={4}>
          <AdminDateRange value={dateRange} onChange={applyDateRange} allowAllTime />
        </Box>

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Check in log could not be loaded" description={error} onRetry={() => loadSwipeLog()} />
        )}

        {/* Loading State */}
        {loading && !hasLoaded ? (
          <AdminState status="loading" title="Loading check in log…" />
        ) : error ? null : swipeLog.length === 0 ? (
          <AdminState title="No check-ins found" description="There are no visits for the selected dates. Try adjusting the date range." />
        ) : (
          <>
            <VStack align="stretch" spacing={2} className="swipe-log-list">
              {swipeLog.map((entry, index) => (
                <Box key={index} className="swipe-log-card">
                  <HStack
                    justify="space-between"
                    className="admin-record-heading"
                    cursor="pointer"
                    onClick={() => toggleRow(index)}
                  >
                    <VStack align="start" spacing={1} minW={0} flex={1}>
                      <HStack spacing={0} gap={2} flexWrap="wrap">
                        <Text fontWeight="bold">
                          {entry.first_name} {entry.last_name}
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          {[entry.user_id, entry.directory_id].filter(value => value !== null && value !== undefined && value !== '').join(' • ')}
                        </Text>
                      </HStack>
                      <HStack spacing={0} gap={2} flexWrap="wrap" fontSize="sm" color="gray.600">
                        <Text>In: {formatDateTime(entry.started_at)}</Text>
                        <ArrowRightIcon boxSize={4} />
                        <Text>
                          Out: {entry.ended_at ? formatDateTime(entry.ended_at) : "—"}
                        </Text>
                        <Badge
                          colorScheme={entry.ended_at ? "green" : "orange"}
                        >
                          {formatDuration(entry.started_at, entry.ended_at)}
                        </Badge>
                        {entry.force_ended && (
                          <Badge colorScheme="blue">Admin checkout</Badge>
                        )}
                        {entry.auto_ended && !entry.force_ended && (
                          <Badge colorScheme="yellow">Auto</Badge>
                        )}
                        <Text>•</Text>
                        <Text title={entry.reason || "No purpose provided"} maxW="200px" isTruncated color={entry.reason ? "gray.600" : "gray.600"}>
                          Purpose: {entry.reason || "No purpose selected"}
                        </Text>
                      </HStack>
                    </VStack>

                    <HStack className="admin-record-actions">
                      {entry.tools_used?.length > 0 && (
                        <Badge colorScheme="purple">
                          {entry.tools_used.length} tool instance{entry.tools_used.length !== 1 ? "s" : ""}
                        </Badge>
                      )}
                      <IconButton
                        icon={expandedRows.has(index) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        size="sm"
                        variant="ghost"
                        aria-label={expandedRows.has(index) ? "Collapse tool usage" : "Expand tool usage"}
                        aria-expanded={expandedRows.has(index)}
                        onClick={event => { event.stopPropagation(); toggleRow(index); }}
                      />
                    </HStack>
                  </HStack>

                  <Collapse in={expandedRows.has(index)} animateOpacity>
                    <Box mt={3} pt={3} borderTop="1px solid #e2e8f0">
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>
                        Tool instances used:
                      </Text>
                      {entry.tools_used?.length > 0 ? (
                        <HStack flexWrap="wrap" spacing={2}>
                          {entry.tools_used.map((machine, mIdx) => (
                            <Tag key={mIdx} size="md" colorScheme="teal" variant="subtle">
                              <TagLabel>
                                {machine.name}
                                {machine.reason && ` (${machine.reason})`}
                              </TagLabel>
                            </Tag>
                          ))}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600">
                          No tool instances used during this session
                        </Text>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </VStack>

            {/* Pagination */}
            <AdminPagination page={page} totalPages={totalPages} totalItems={total} pageSize={50} isLoading={loading} onPageChange={setPage} itemLabel="records" />
          </>
        )}
      </div>
    </Slider>
  );
}

LabSwipeLogTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default LabSwipeLogTab;
